import React, { useState, useEffect } from "react";
import { useMessageApi } from "../../../shared/providers/MessageProvider";
import uploadIcon from "../../../assets/images/upload-icon.svg";

/**
 * DocumentsSection Component
 * Handles multiple file uploads for documents with drag & drop UI.
 * Props:
 *  - config: { hasSimpleForm: boolean }
 *  - fileRef: ref for document file input
 *  - handleFileChange: callback for file change
 *  - resetFileInfo: boolean to reset file info
 */
const DocumentsSection = ({
  config,
  fileRef,
  handleFileChange,
  resetFileInfo = false,
}) => {
  // Message API from MessageProvider for toast notifications
  const message = useMessageApi();

  // State to store multiple files information
  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // Reset files when resetFileInfo prop changes
  useEffect(() => {
    if (resetFileInfo) {
      setFiles([]);
    }
  }, [resetFileInfo]);

  /**
   * Remove a specific file from the files array
   * @param {number} index - Index of the file to remove
   */
  const handleRemoveFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    
    // Call the parent handler with updated files
    if (handleFileChange) {
      const syntheticEvent = { target: { files: updatedFiles } };
      handleFileChange(syntheticEvent, "file");
    }
  };

  // Drag and drop handlers
  const handleDrag = (e, isOver) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(isOver);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFilesSelection(droppedFiles);
  };

  const handleUploadAreaClick = () => {
    if (fileRef.current) {
      fileRef.current.click();
    }
  };

  /**
   * Handle file selection with validation for max 5 files
   * @param {FileList|Array} selectedFiles - Files to be added
   */
  const handleFilesSelection = (selectedFiles) => {
    const filesArray = Array.from(selectedFiles);
    const currentFileCount = files.length;
    const newFileCount = filesArray.length;
    
    console.log('handleFilesSelection called:', {
      filesArray: filesArray.map(f => f.name),
      currentFileCount,
      newFileCount,
      currentFiles: files.map(f => f.name)
    });
    
    // Check if adding these files would exceed the limit
    if (currentFileCount + newFileCount > 5) {
      const remainingSlots = 5 - currentFileCount;
      if (remainingSlots <= 0) {
        message.error(`File limit reached! You already have ${currentFileCount} file(s). Please remove some files before adding more.`, 3);
      } else {
        message.error(`Too many files selected! You can only add ${remainingSlots} more file(s). Please select fewer files.`, 3);
      }
      return;
    }
    
    // Add new files to existing files
    const updatedFiles = [...files, ...filesArray];
    setFiles(updatedFiles);
    
    console.log('Updated files:', updatedFiles.map(f => f.name));
    
    // Call the parent handler with the complete array of files
    if (handleFileChange) {
      const syntheticEvent = { target: { files: updatedFiles } };
      handleFileChange(syntheticEvent, "file");
    }
  };

  /**
   * Handle file input change
   * @param {object} e - event object
   */
  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFilesSelection(selectedFiles);
    
    // Reset the file input value to allow selecting the same files again
    e.target.value = '';
  };

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /**
   * Render individual document section for each file
   * @param {File} file - The file object
   * @param {number} index - Index of the file
   */
  const renderDocumentSection = (file, index) => (
    <div key={`${file.name}-${index}`} className="col-3 mb-3">
      <div className="upload-file position-relative">
        <label className="file">
          <div className="file-name-container">
            {file.name}
          </div>
          <span className="sml">
            type: {file.type}, {formatFileSize(file.size)}
          </span>
          <div className="mt-2">
            <small className="text-muted">Document {index + 1}</small>
          </div>
        </label>
        {/* Remove file button */}
        <button
          type="button"
          className="btn-close-file"
          aria-label={`Remove ${file.name}`}
          onClick={() => handleRemoveFile(index)}
          tabIndex={0}
        >
          <i className="bi bi-x-circle-fill"></i>
        </button>
      </div>
    </div>
  );

  return (
    <div className="row mt-3">
      {/* Render Documents upload section only if simple form is not enabled */}
      {!config.hasSimpleForm && (
        <>
          <div className="col-12">
            <p>Documents</p>
          </div>
          
          {/* Show upload area when files are less than 5 */}
          {files.length < 5 && (
            <div className="col-3">
              <div 
                className={`upload-file position-relative ${isDragOver ? 'drag-over' : ''}`}
                onDragOver={(e) => handleDrag(e, true)}
                onDragEnter={(e) => handleDrag(e, true)}
                onDragLeave={(e) => handleDrag(e, false)}
                onDrop={handleDrop}
                onClick={handleUploadAreaClick}
                style={{ cursor: 'pointer' }}
              >
                <label className="file" onClick={(e) => e.stopPropagation()}>
                  <div>
                    <img className="mb-2" src={uploadIcon} alt="upload-icon" />
                  </div>
                  <div>
                    Drag & Drop Files or <span className="browse-text">Browse</span>
                  </div>
                  <small className="text-muted">
                    {files.length === 0 ? 'Max 5 files' : `${5 - files.length} more file(s) allowed`}
                  </small>
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileRef}
                    onChange={handleFileInputChange}
                    multiple
                    autoComplete="off"
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>
          )}
          
          {/* Show individual document sections for each file */}
          {files.map((file, index) => renderDocumentSection(file, index))}
        </>
      )}
    </div>
  );
};

export default DocumentsSection;
